// COS-BRIEF-RULES: the "Rules" list of the CEO's brief settings — who else gets the Chief of
// Staff report (the owner, a person, or the CEO's own tuning), which sections, how often and by
// which channel. The CoS sets these when the CEO asks in chat; the CEO (or an admin) can edit or
// remove each one here. `briefRules.*` (packages/schema/src/contracts/api/de-briefs.ts).
import type { OperationInput, OperationOutput } from "@companyos/sdk";
import {
  Button,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Segmented,
  Toggle,
  toast,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import { RowAction, RowActions } from "../settings/row-actions";

type RulesView = OperationOutput<"briefRules.list">;
type Rule = RulesView["rules"][number];
type Put = OperationInput<"briefRules.put">["body"];
type Section = Rule["sections"][number];
type Channel = Rule["channel"];
type Frequency = Rule["frequency"];

export const rulesKey = (co: string) => ["company", co, "brief-rules"] as const;
export const rulesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: rulesKey(co),
    queryFn: () => api.client.briefRules.list({ params: { co } }),
  });

const SECTIONS: { value: Section; label: string }[] = [
  { value: "work", label: "Work" },
  { value: "kpis", label: "KPIs" },
  { value: "stuck", label: "Stuck" },
  { value: "decisions", label: "Decisions" },
];
const CHANNELS: { value: Channel; label: string }[] = [
  { value: "inbox", label: "Inbox" },
  { value: "dm", label: "DM" },
  { value: "email", label: "Email" },
];
const FREQUENCIES: { value: Frequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "custom", label: "Custom" },
];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

const sectionsLabel = (s: readonly Section[]) =>
  SECTIONS.filter((x) => s.includes(x.value))
    .map((x) => x.label)
    .join(", ");

/** "Mon–Fri 08:00", "Fri 16:00", "Mon, Thu 09:00". */
export function ruleRhythm(r: Pick<Rule, "frequency" | "days" | "time">): string {
  const days =
    r.frequency === "daily"
      ? "Mon–Fri"
      : r.frequency === "weekly"
        ? DAYS[(r.days?.[0] ?? 5) - 1]
        : (r.days ?? []).map((d) => DAYS[d - 1]).join(", ");
  return `${days} ${r.time}`;
}

const whoLabel = (r: Rule, v: RulesView) =>
  r.recipient.kind === "owner"
    ? `Owner${r.recipient.name ? ` (${r.recipient.name})` : ""}`
    : r.recipient.membership_id === v.ceo?.membership_id
      ? `CEO (${r.recipient.name ?? ""})`
      : (r.recipient.name ?? "Unknown");

/** The rules list, for the CEO's (or a company's) brief settings. Hidden from others. */
export function BriefRules({ api, co }: { api: Api; co: string }) {
  const q = useQuery(rulesQuery(api, co));
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) => api.client.briefRules.delete({ params: { co, id } }),
    onSuccess: (v) => {
      qc.setQueryData(rulesKey(co), v);
      toast.add({ title: "Rule removed", type: "success" });
    },
  });
  const v = q.data;
  if (!v?.can_edit) return null;
  return (
    <div className="flex flex-col gap-2 border-t border-subtle pt-4" data-slot="brief-rules">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium">Rules</span>
          <InfoTip label="About rules">
            Who else gets the Chief of Staff report, what, how often and how. Ask your Chief of
            Staff in chat, or set them here.
            {v.owner_is_ceo ? " The owner is the CEO, so no owner rule is needed." : ""}
          </InfoTip>
        </div>
        {v.can_edit && editing === null ? (
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing("new")}>
            <PlusIcon aria-hidden="true" />
            Add rule
          </Button>
        ) : null}
      </div>
      {v.rules.length === 0 && editing !== "new" ? (
        <p className="text-xs text-muted-foreground">Only the CEO gets the report.</p>
      ) : null}
      <ul className="flex flex-col gap-1">
        {v.rules.map((r) =>
          editing === r.id ? (
            <li key={r.id}>
              <RuleEditor api={api} co={co} view={v} rule={r} onDone={() => setEditing(null)} />
            </li>
          ) : (
            <li
              key={r.id}
              className="group/row flex items-center justify-between gap-2 rounded-md px-1 py-1 text-sm hover:bg-muted/50"
              data-slot="brief-rule"
            >
              <div className="flex min-w-0 flex-col">
                <span className="flex items-center gap-1 font-medium">
                  {whoLabel(r, v)}
                  {r.note ? <InfoTip label="Not sending">{r.note}</InfoTip> : null}
                </span>
                <span
                  className={`truncate text-xs ${r.applies ? "text-muted-foreground" : "text-muted-foreground line-through"}`}
                >
                  {r.enabled
                    ? `${sectionsLabel(r.sections)} · ${ruleRhythm(r)} · ${CHANNELS.find((c) => c.value === r.channel)?.label}`
                    : "Nothing"}
                </span>
              </div>
              {v.can_edit ? (
                <RowActions>
                  <RowAction
                    icon={PencilIcon}
                    label={`Edit rule for ${whoLabel(r, v)}`}
                    onClick={() => setEditing(r.id)}
                  />
                  <RowAction
                    icon={Trash2Icon}
                    label={`Remove rule for ${whoLabel(r, v)}`}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(r.id)}
                  />
                </RowActions>
              ) : null}
            </li>
          ),
        )}
        {editing === "new" ? (
          <li>
            <RuleEditor api={api} co={co} view={v} rule={null} onDone={() => setEditing(null)} />
          </li>
        ) : null}
      </ul>
      {remove.error ? (
        <Notice tone="danger" role="alert">
          {problemText(remove.error, "The rule could not be removed.")}
        </Notice>
      ) : null}
    </div>
  );
}

type Draft = {
  recipient: string;
  sections: Section[];
  frequency: Frequency;
  days: number[];
  time: string;
  channel: Channel;
};

/** One rule's fields, inline (no nested form: the settings modal is one already). */
function RuleEditor({
  api,
  co,
  view,
  rule,
  onDone,
}: {
  api: Api;
  co: string;
  view: RulesView;
  rule: Rule | null;
  onDone: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const [d, setD] = useState<Draft>(() =>
    rule
      ? {
          recipient:
            rule.recipient.kind === "owner" ? "owner" : (rule.recipient.membership_id ?? ""),
          sections: [...rule.sections],
          frequency: rule.frequency,
          days: rule.days ?? [5],
          time: rule.time,
          channel: rule.channel,
        }
      : {
          // The owner's update: weekly by default, never daily.
          recipient: view.owner_is_ceo ? (view.ceo?.membership_id ?? "") : "owner",
          sections: ["work", "kpis", "decisions"],
          frequency: "weekly",
          days: [5],
          time: "16:00",
          channel: "inbox",
        },
  );
  const save = useMutation({
    mutationFn: (body: Put) => api.client.briefRules.put({ params: { co }, body }),
    onSuccess: (v) => {
      qc.setQueryData(rulesKey(co), v);
      toast.add({ title: "Rule saved", type: "success" });
      onDone();
    },
  });
  const set = (p: Partial<Draft>) => {
    save.reset();
    setD((x) => ({ ...x, ...p }));
  };
  const toggleDay = (n: number, on: boolean) => {
    if (d.frequency === "weekly") return on ? set({ days: [n] }) : undefined;
    const next = on ? [...new Set([...d.days, n])] : d.days.filter((x) => x !== n);
    if (next.length) set({ days: next.sort((a, b) => a - b) });
  };
  const submit = () =>
    save.mutate({
      recipient:
        d.recipient === "owner"
          ? { kind: "owner" }
          : { kind: "person", membership_id: d.recipient },
      sections: d.sections,
      frequency: d.frequency,
      days: d.frequency === "daily" ? null : d.days,
      time: d.time,
      channel: d.channel,
      enabled: rule?.enabled ?? true,
    });
  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-subtle p-3"
      data-slot="brief-rule-editor"
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-to`}>To</Label>
        <NativeSelect
          id={`${id}-to`}
          className="w-full"
          disabled={rule !== null}
          value={d.recipient}
          onChange={(e) => set({ recipient: e.target.value })}
        >
          {view.owner_is_ceo ? null : (
            <NativeSelectOption value="owner">
              Owner{view.owner ? ` (${view.owner.name})` : ""}
            </NativeSelectOption>
          )}
          {view.recipient_choices.map((p) => (
            <NativeSelectOption key={p.membership_id} value={p.membership_id}>
              {p.membership_id === view.ceo?.membership_id ? `${p.name} (CEO)` : p.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
      <fieldset className="flex flex-col gap-1.5">
        <span className="flex items-center gap-1 text-sm font-medium">
          Sections
          <InfoTip label="About sections">
            The parts of the report they get. Work: who is doing what. Stuck: what is blocked.
            Decisions: what waits on approval.
          </InfoTip>
        </span>
        <div className="flex flex-wrap gap-1">
          {SECTIONS.map((s) => (
            <Toggle
              key={s.value}
              variant="chip"
              size="sm"
              aria-label={s.label}
              pressed={d.sections.includes(s.value)}
              onPressedChange={(on) => {
                const next = on
                  ? [...d.sections, s.value]
                  : d.sections.filter((x) => x !== s.value);
                if (next.length) set({ sections: next });
              }}
            >
              {s.label}
            </Toggle>
          ))}
        </div>
      </fieldset>
      <Segmented
        aria-label="How often"
        size="sm"
        options={FREQUENCIES.map((f) => ({
          value: f.value,
          label: f.value === "daily" ? "Daily (Mon–Fri)" : f.label,
        }))}
        value={d.frequency}
        onValueChange={(f) =>
          set({ frequency: f, days: f === "weekly" ? [d.days[0] ?? 5] : d.days })
        }
      />
      {d.frequency !== "daily" ? (
        <fieldset className="flex flex-wrap gap-1" aria-label="Days">
          {DAYS.map((label, i) => (
            <Toggle
              key={label}
              variant="chip"
              size="sm"
              aria-label={label}
              pressed={d.days.includes(i + 1)}
              onPressedChange={(on) => toggleDay(i + 1, on)}
            >
              {label}
            </Toggle>
          ))}
        </fieldset>
      ) : null}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-time`}>Time</Label>
          <Input
            id={`${id}-time`}
            type="time"
            className="h-9 w-32"
            value={d.time}
            onChange={(e) => e.target.value && set({ time: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">Channel</span>
          <Segmented
            aria-label="Channel"
            size="sm"
            options={CHANNELS}
            value={d.channel}
            onValueChange={(c) => set({ channel: c })}
          />
        </div>
      </div>
      {save.error ? (
        <Notice tone="danger" role="alert">
          {problemText(save.error, "The rule could not be saved.")}
        </Notice>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={save.isPending || !d.recipient} onClick={submit}>
          {save.isPending ? "Saving…" : "Save rule"}
        </Button>
      </div>
    </div>
  );
}
